// German team-name overrides for API-Football's English-only names.
//
// Why client-side instead of patching the DB:
//   The cron sync writes whatever API-Football returns. If we patched the
//   stored values, the next sync would overwrite them. Doing the translation
//   at display time keeps the DB as the canonical "source of truth from
//   API-Football", and the rendering layer applies the German overlay.
//
// Coverage: WC 2026 includes the FIFA-recognised pool of ~48 national teams.
// We list the most likely participants; teams not in the map render with
// their English name as fallback. That degrades gracefully — nothing breaks
// if API-Football introduces a team we haven't mapped yet.
//
// Used by: GameCard, StandingsTable, KOBracket, Onboarding bonus dropdowns,
// any other place that displays a team name pulled from the DB.

const TEAM_NAMES_DE: Record<string, string> = {
  // Europe
  Germany: "Deutschland",
  Austria: "Österreich",
  Netherlands: "Niederlande",
  Belgium: "Belgien",
  France: "Frankreich",
  Spain: "Spanien",
  Italy: "Italien",
  Switzerland: "Schweiz",
  Croatia: "Kroatien",
  Czech: "Tschechien",
  "Czech Republic": "Tschechien",
  Czechia: "Tschechien",
  Denmark: "Dänemark",
  England: "England",
  Wales: "Wales",
  Scotland: "Schottland",
  "Republic of Ireland": "Irland",
  Ireland: "Irland",
  Poland: "Polen",
  Hungary: "Ungarn",
  Romania: "Rumänien",
  Greece: "Griechenland",
  Norway: "Norwegen",
  Sweden: "Schweden",
  Iceland: "Island",
  Slovakia: "Slowakei",
  Slovenia: "Slowenien",
  Serbia: "Serbien",
  "Bosnia & Herzegovina": "Bosnien & Herzegowina",
  "Bosnia and Herzegovina": "Bosnien & Herzegowina",
  Portugal: "Portugal",
  Türkiye: "Türkei",
  Turkey: "Türkei",
  Ukraine: "Ukraine",
  Russia: "Russland",

  // Americas
  Argentina: "Argentinien",
  Brazil: "Brasilien",
  Chile: "Chile",
  Colombia: "Kolumbien",
  Ecuador: "Ecuador",
  Mexico: "Mexiko",
  "United States": "USA",
  USA: "USA",
  Canada: "Kanada",
  "Costa Rica": "Costa Rica",
  Uruguay: "Uruguay",
  Peru: "Peru",
  Paraguay: "Paraguay",
  Panama: "Panama",
  Jamaica: "Jamaika",
  Haiti: "Haiti",
  "Cape Verde Islands": "Kap Verde",
  "Cape Verde": "Kap Verde",
  "Curaçao": "Curaçao",
  Curacao: "Curaçao",

  // Africa
  Egypt: "Ägypten",
  Morocco: "Marokko",
  Tunisia: "Tunesien",
  Algeria: "Algerien",
  Senegal: "Senegal",
  Nigeria: "Nigeria",
  Ghana: "Ghana",
  "Ivory Coast": "Elfenbeinküste",
  Cameroon: "Kamerun",
  "South Africa": "Südafrika",
  "Congo DR": "DR Kongo",
  "DR Congo": "DR Kongo",

  // Asia / Oceania
  Japan: "Japan",
  "South Korea": "Südkorea",
  "Korea Republic": "Südkorea",
  "North Korea": "Nordkorea",
  Australia: "Australien",
  "New Zealand": "Neuseeland",
  "Saudi Arabia": "Saudi-Arabien",
  Qatar: "Katar",
  Iran: "Iran",
  Iraq: "Irak",
  Israel: "Israel",
  Uzbekistan: "Usbekistan",
  Jordan: "Jordanien",
};

/**
 * Render a team name in German if a translation exists, otherwise the
 * original (English) name. Safe to call with `undefined` / empty.
 */
export function teamDe(name: string | null | undefined): string {
  if (!name) return "";
  return TEAM_NAMES_DE[name] ?? name;
}
